import { useState } from 'react';
import { Star, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export const ReviewForm = ({ targetType, targetId, onSuccess }) => {
  const { isAuthenticated, user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photoUrls.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    // For demo, we'll use placeholder URLs
    // In production, you'd upload to a storage service
    const newPhotoUrls = files.map((file) => {
      return URL.createObjectURL(file);
    });
    
    setPhotos([...photos, ...files]);
    setPhotoUrls([...photoUrls, ...newPhotoUrls]);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newUrls = photoUrls.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    setPhotoUrls(newUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to leave a review');
      return;
    }

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setLoading(true);
    try {
      // In production, upload photos to storage first
      // For now, using placeholder URLs
      const uploadedPhotoUrls = photoUrls.length > 0 ? [
        'https://images.unsplash.com/photo-1642721085217-b5f5b6b47d0a?w=400',
        ...photoUrls.slice(1).map(() => 'https://images.unsplash.com/photo-1581378670696-6bb27d6e450b?w=400')
      ] : [];

      await api.post('/reviews', {
        target_type: targetType,
        target_id: targetId,
        rating,
        comment,
        photos: uploadedPhotoUrls
      });

      toast.success('Review posted successfully!');
      setRating(0);
      setComment('');
      setPhotos([]);
      setPhotoUrls([]);
      
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post review');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Please sign in to leave a review</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="review-form">
      <CardHeader>
        <CardTitle>Write a Review</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Star Rating */}
          <div>
            <label className="text-sm font-medium mb-2 block">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-medium mb-2 block">Your Review</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              rows={4}
              required
              data-testid="review-comment-input"
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="text-sm font-medium mb-2 block">Add Photos (Optional)</label>
            <div className="space-y-3">
              {photoUrls.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {photoUrls.map((url, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border group">
                      <img
                        src={url}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-photo-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {photoUrls.length < 5 && (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-indigo-600 transition-colors">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Add photos ({photoUrls.length}/5)
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            disabled={loading}
            data-testid="submit-review-btn"
          >
            {loading ? 'Posting...' : 'Post Review'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
